import { formatURLTitle } from './http.js';
import moment from 'moment';

const buildInternalSubscriptionUrl = (subscription) => {
  // check if they are subscribed, if they are just send em to the article page
  // but don't include an article id so they just get the latest article they have
  // subscribed to
  if (subscription.subscribed || subscription.expired) {
    return buildInternalArticleUrl(null, subscription);
  }
  // subscriptionId/:subscriptionTitle
  return `/subscriptions/${subscription.id}/${formatURLTitle(subscription.title)}`;
};

const buildInternalArticleUrl = (article, subscription) => {
  if (article) {
    // subscriptionId/:subscriptionTitle/articles/:articlePublicId/:articleTitle
    return `/subscriptions/${subscription.id}/${formatURLTitle(subscription.title)}/articles/${article.publicId}/${formatURLTitle(article.title)}`;
  }
  // subscriptionId/:subscriptionTitle/articles
  return `/subscriptions/${subscription.id}/${formatURLTitle(subscription.title)}/articles`;
};

const isActiveSubscription = (user, subscriptionId) => {
  let isActive = false;
  if (user.activeSubscriptions && user.activeSubscriptions.length > 0) {
    const activeSubscriptionId = user.activeSubscriptions.find(as => as === subscriptionId);
    isActive = activeSubscriptionId !== undefined;
  }
  return isActive;
};

const isExpiredSubscription = (user, subscriptionId) => {
  let hasExpired = false;
  if (user.expiredSubscriptions && user.expiredSubscriptions.length > 0) {
    const expiredSubscriptionId = user.expiredSubscriptions.find(es => es === subscriptionId);
    hasExpired = expiredSubscriptionId !== undefined;
  }
  return hasExpired;
};

const hasHadAnySubscriptions = (user) => {
  if ((user.activeSubscriptions && user.activeSubscriptions.length > 0) ||
    (user.expiredSubscriptions && user.expiredSubscriptions.length > 0)
  ) {
    return true;
  }
  return false;
};

const hasActiveSubscription = (user) => {
  if (user.activeSubscriptions && user.activeSubscriptions.length > 0) {
    return true;
  }
  return false;
};

const getPlanByType = (subscription, planType) => {
  // Null-safe: subscriptionById can return null (e.g. an invalid/removed sub id in
  // the URL), which would otherwise throw on .subscriptionPlans at every call site.
  if (!subscription || !subscription.subscriptionPlans) {
    return null;
  }
  const foundPlan = subscription.subscriptionPlans.find(plan => plan.type.toLowerCase() === planType);
  // A miss must be null, not `{ ...undefined }`. An active subscription should always
  // have exactly a monthly and a yearly plan, but nothing enforces that invariant on
  // subscriptionById (it serves drafts/off-sale ids too) — callers branch on null to
  // render fallbacks instead of crashing on incomplete data.
  if (!foundPlan) {
    return null;
  }
  const plan = { ...foundPlan };
  plan.subscription = subscription;
  return plan;
};

const subscriptionContainsPublishedArticle = (subscription) => {
  let containsPublishedArticle = false;
  if (subscription.articles) {
    const publicArticles = subscription.articles.filter(article => article.status === 'Published');
    containsPublishedArticle = publicArticles && publicArticles.length > 0;
  }
  return containsPublishedArticle;
};

// check to see if all hands attached to an article are free, only then can an article be marked as free
const allHandsAreFree = (hands) => {
  let allFreeHands = false;
  if (hands && hands.length > 0) {
    const notFreeHands = hands.filter(h => !h.freeToPlay);
    allFreeHands = notFreeHands.length === 0;
  }
  return allFreeHands;
};

// Bucket articles into calendar days using UTC, to agree with the server's UTC month
// window (articlesByMonthAndSubscription) and so every reader sees an article on the same
// day regardless of their browser timezone (the bare moment() grid skewed by local day).
const getArticlesForMonth = (articles, month, year) => {
  const articleForMonth = articles.filter(a => moment.utc(a.publishDateTime).month() === month && moment.utc(a.publishDateTime).year() === year);
  return articleForMonth.map(a => moment.utc(a.publishDateTime).date());
};

const getArticleByDate = (articles, date) => {
  // date should be in ISO format; matched on the UTC calendar day to agree with the grid
  // above (matched on the leading YYYY-MM-DD, so .utc() is intent — a no-op for ISO inputs).
  const justDate = moment.utc(date, 'YYYY-MM-DD');
  const foundArticle = articles.find(a => {
    const justArticleDate = moment.utc(a.publishDateTime, 'YYYY-MM-DD');
    if (justDate.isSame(justArticleDate)) {
      return a;
    }
    return null;
  });
  return foundArticle;
};

// Floor the calendar's back-nav at the subscription's first-article month.
// `firstArticleDate` is the server's subscriptionFirstArticleDate — a UTC
// 'YYYY-MM-DD' string, or null/empty when the subscription has no owned+published
// articles. Returns the start of that month (UTC, to agree with the badge/by-date
// queries) for use as the DateCalendar `minDate`, or null when there is nothing to
// floor (graceful — the calendar simply imposes no lower bound).
const getCalendarBackNavFloor = (firstArticleDate) => {
  if (!firstArticleDate) {
    return null;
  }
  return moment.utc(firstArticleDate, 'YYYY-MM-DD').startOf('month');
};

export {
  buildInternalSubscriptionUrl,
  buildInternalArticleUrl,
  isActiveSubscription,
  isExpiredSubscription,
  hasHadAnySubscriptions,
  hasActiveSubscription,
  getPlanByType,
  subscriptionContainsPublishedArticle,
  allHandsAreFree,
  getArticlesForMonth,
  getArticleByDate,
  getCalendarBackNavFloor
};
